import React, { useEffect, useState, forwardRef, useContext } from 'react';
import { HotTable } from '@handsontable/react';
import {
    SexRender, SpeciesCodeRender, DateRender, ringNoRenderer, SpeciesRender,
    LocalityRender
} from "./HandOnRender";
import AppContext from '../../../Context/AppContext';
import CacheManger from "../../../Utils/CacheManage";
import CreateGetFetch from "../../../ServerCalls/CreateGetFetch";

const HandsOnTableResighting = forwardRef((
    {
        TableData = [],
        delRowsCallback = () => { console.log("NoParam"); },
        updatedRowsCallBack = () => { console.log("NoParam"); }
    }, passedRef) => {

    const [cols, setCols] = useState([]);
    const [rows, setRows] = useState([]);
    const [error, setError] = useState([]);

    // Default headers for resighting page
    const defaultHeaders = [
        "Sr. No.",
        "Ring No",
        "Ring Size",
        "Species Code",
        "Scientific Name",
        "Sex",
        "Reported By",
        "Place of Recovery",
        "Latitude",
        "Longitude",
        "Date of Recovery",
        "Remarks",
        "Date of Ringing",
        "Place of Ringing",
        "Ringer",
        "QC Issues faced"
    ];

    const cacheManager = new CacheManger();
    const getObject = CreateGetFetch();

    // Dynamic columns that should always appear in this specific order
    const dynamicColumns = [
        "valid",
        "database_id",
        "Ringing Record Found?",
        "Uploaded to Avifauna?"
    ];

    // Dropdown configurations
    const dropdownOptions = {
        "Ring Size": ["A", "AB", "B", "C", "F", "AA", "BC", "G", "K", "L", "M", "YZ", "Z", "NA"],
        "RingSize": ["A", "AB", "B", "C", "F", "AA", "BC", "G", "K", "L", "M", "YZ", "Z", "NA"],
        "Ring size": ["A", "AB", "B", "C", "F", "AA", "BC", "G", "K", "L", "M", "YZ", "Z", "NA"],
        "Sex": ["", "Male", "Female", "Unknown"],
        "Scx": ["", "Male", "Female", "Unknown"]
    };

    // Helper function to sort headers: default headers first, then dynamic columns in order, then other new columns
    const sortHeaders = (headers) => {
        if (!headers || !Array.isArray(headers)) return headers;
        
        return [...headers].sort((a, b) => {
            const indexA = defaultHeaders.indexOf(a);
            const indexB = defaultHeaders.indexOf(b);
            const dynamicIndexA = dynamicColumns.indexOf(a);
            const dynamicIndexB = dynamicColumns.indexOf(b);

            // Both are default headers - maintain their order
            if (indexA !== -1 && indexB !== -1) {
                return indexA - indexB;
            }
            // Only A is a default header - A comes first
            else if (indexA !== -1) {
                return -1;
            }
            // Only B is a default header - B comes first
            else if (indexB !== -1) {
                return 1;
            }
            // Both are dynamic columns - maintain their specific order
            else if (dynamicIndexA !== -1 && dynamicIndexB !== -1) {
                return dynamicIndexA - dynamicIndexB;
            }
            // Only A is a dynamic column - A comes before other new columns
            else if (dynamicIndexA !== -1) {
                return -1;
            }
            // Only B is a dynamic column - B comes before other new columns
            else if (dynamicIndexB !== -1) {
                return 1;
            }
            // Neither is a default or dynamic column - maintain their relative order (both go to end)
            else {
                return 0;
            }
        });
    };

    const mapDataToExistingColumns = (dataArray, existingHeaders) => {
        const columnMapping = {
            "Sr. No.": ["No.", "No", "Sr.No.","Sr.No","Sr. No"],
            "Ring No": ["Ring No.","Ring No","RingNo","ring_number"],
            "Ring Size": ["Ring Size","RingSize","Ring size"],
            "Sex": ["Sex","Scx"],
            "Reported By": ["Reported by", "Reported By"],
            "Place of Recovery": ["Place of Recovery","Place of recovery"],
            "Date of Recovery": ["Date of Recovery","Date of recovery"],
            "Remarks": ["Remarks"],
            "Date of Ringing": ["Date of Ringing","Date of ringing"],
            "Place of Ringing": ["Place of Ringing","Place of ringing"],
            "Ringer": ["Ringer"],
            "Scientific Name": ["Species", "Scientific Name","species"],
            "Species Code": ["SpeciesCode", "Species Code"],
            "Latitude": ["Latitude"],
            "Longitude": ["Longitude"],
        };

        const rowCount = dataArray.length > 0 ? dataArray.length : 10;
   
        return Array(rowCount).fill(null).map((_, rowIndex) => {
            const sourceRow = dataArray[rowIndex] || {};
            const mappedRow = {};
           
            existingHeaders.forEach(header => {
                const possibleSourceKeys = columnMapping[header] || [];
                let matchedValue = "";

                for (const sourceKey of possibleSourceKeys) {
                    if (sourceRow[sourceKey] !== undefined && sourceRow[sourceKey] !== null) {
                        matchedValue = sourceRow[sourceKey];
                        break;
                    }
                }
               
                mappedRow[header] = matchedValue;
            });
           
            return mappedRow;
        });
    };

    // Custom dropdown renderer - shows value even if not in options (validation happens on submit/merge/remerge)
    const dropdownRenderer = (options) => {
        return (instance, td, row, col, prop, value, cellProperties) => {
            // Get the actual column name from updated_headers (this matches what's in errorList)
            const actualColName = (TableData.updated_headers && TableData.updated_headers[col]) 
                ? TableData.updated_headers[col] 
                : prop;
            
            // Check for errors in errorList - try multiple column name variations
            let errorList = null;
            if (TableData.errorList) {
                // Build list of possible column names to try
                const possibleColNames = [prop, actualColName];
                
                // Add variations for Ring Size columns
                if (prop === "Ring Size" || prop === "RingSize" || prop === "Ring size" || 
                    actualColName === "Ring Size" || actualColName === "RingSize" || actualColName === "Ring size") {
                    possibleColNames.push("Ring Size", "RingSize", "Ring size");
                }
                // Add variations for Sex columns
                if (prop === "Sex" || prop === "Scx" || actualColName === "Sex" || actualColName === "Scx") {
                    possibleColNames.push("Sex", "Scx");
                }
                
                // Remove duplicates
                const uniqueColNames = [...new Set(possibleColNames)];
                
                // Try each variation with row as both string and number
                for (const colNameVar of uniqueColNames) {
                    errorList = TableData.errorList['' + row + '_' + colNameVar];
                    if (errorList) break;
                    errorList = TableData.errorList[row + '_' + colNameVar];
                    if (errorList) break;
                }
            }
            
            // Clear the cell
            td.innerHTML = '';
            td.style.padding = '0';
            
            // Create a select element
            const select = document.createElement('select');
            select.style.width = '100%';
            select.style.height = '100%';
            select.style.border = 'none';
            select.style.padding = '4px';
            select.style.fontFamily = 'inherit';
            select.style.fontSize = 'inherit';
            select.style.backgroundColor = 'transparent';
            select.style.cursor = 'pointer';
            
            // Check if current value is in options
            const valueExists = options && Array.isArray(options) && options.includes(value);
            const hasValue = value !== null && value !== undefined && value !== '';
            
            // Check if value doesn't match any option - this indicates an invalid value
            const isInvalidValue = hasValue && !valueExists && options && Array.isArray(options);
            
            // Check if this is a Ring Size column - if so, add blank option first
            const isRingSizeColumn = prop === "Ring Size" || prop === "RingSize" || prop === "Ring size" || 
                                     actualColName === "Ring Size" || actualColName === "RingSize" || actualColName === "Ring size";
            
            // Add blank option first for Ring Size columns (to allow keeping it blank)
            if (isRingSizeColumn) {
                const blankOption = document.createElement('option');
                blankOption.value = '';
                blankOption.text = '';
                blankOption.selected = !hasValue; // Select blank option if value is empty
                select.appendChild(blankOption);
            }
            
            // Add only valid options to the dropdown (don't add invalid values as selectable options)
            if (options && Array.isArray(options)) {
                options.forEach(opt => {
                    const option = document.createElement('option');
                    option.value = opt;
                    option.text = opt || ''; // Handle empty string
                    // Only select if value matches and it's not blank (blank is handled above)
                    if (opt === value && hasValue) {
                        option.selected = true;
                    }
                    select.appendChild(option);
                });
            }
            
            // If value exists but is not in options, create a hidden disabled option to hold the value
            // This allows the select to display the invalid value, but it won't appear in dropdown
            if (isInvalidValue) {
                const invalidOption = document.createElement('option');
                invalidOption.value = value;
                invalidOption.text = value;
                invalidOption.selected = true;
                invalidOption.disabled = true;
                invalidOption.hidden = true; // Hide from dropdown list but keep value visible in input
                // Insert after blank option if it exists, otherwise at the beginning
                if (isRingSizeColumn && select.firstChild) {
                    select.insertBefore(invalidOption, select.firstChild.nextSibling);
                } else {
                    select.insertBefore(invalidOption, select.firstChild);
                }
            }
            
            // For Ring Size columns: ensure blank value stays blank (don't auto-select first option)
            if (isRingSizeColumn && !hasValue) {
                // Explicitly set select value to empty string to prevent browser from selecting first option
                select.value = '';
                // Also ensure blank option is selected
                if (select.firstChild && select.firstChild.value === '') {
                    select.firstChild.selected = true;
                }
            }
            
            // Determine if we should show yellow background
            const shouldShowYellow = isInvalidValue || (errorList && typeof errorList !== 'undefined' && errorList !== null);
            
            // Apply yellow background if value doesn't match any option OR if there's an error in errorList
            if (shouldShowYellow) {
                td.style.setProperty('background-color', 'yellow', 'important');
            }
            
            // Handle change
            select.addEventListener('change', (e) => {
                instance.setDataAtCell(row, col, e.target.value);
            });
            
            td.appendChild(select);
            
            // Re-apply yellow background after appending select to ensure it's visible
            // Use setProperty with important to prevent Handsontable from overriding it
            if (shouldShowYellow) {
                td.style.setProperty('background-color', 'yellow', 'important');
            } else {
                // Clear background if no error
                td.style.setProperty('background-color', '', 'important');
            }
            
            // Final safeguard: For Ring Size columns with blank value, ensure blank is selected
            if (isRingSizeColumn && !hasValue) {
                setTimeout(() => {
                    if (select.value !== '') {
                        select.value = '';
                    }
                }, 0);
            }
            
            // Prevent event propagation
            select.addEventListener('click', (e) => {
                e.stopPropagation();
            });
        };
    };


    const columnListBuilder = async (list) => {
        if (typeof list !== 'undefined') {
            const fixedList = {
                // "id": {
                //     data: "id",
                //     type: "text",
                //     fixedColumnsStart: true
                // },
                "Sr. No.": {
                    data: "Sr. No.",
                    type: "text",
                    // readOnly: true
                },
                "Ring No": {
                    data: "Ring No",
                    type: "text",
                    renderer: (instance, td, row, col, prop, value, cellProperties) => {
                        ringNoRenderer(instance, td, row, col, prop, value, cellProperties,
                            TableData.errorList['' + row + '_' + prop], TableData.duplicateRingNumbers)
                    },
                },
                "Ring Size": {
                    data: "Ring Size",
                    type: "dropdown",
                    source: ["A", "AB", "B", "C", "F", "AA", "BC", "G", "K", "L", "M", "YZ", "Z", "NA"],
                    renderer: dropdownRenderer(dropdownOptions["Ring Size"]),
                },
                "RingSize": {
                    data: "RingSize",
                    type: "dropdown",
                    source: ["A", "AB", "B", "C", "F", "AA", "BC", "G", "K", "L", "M", "YZ", "Z", "NA"],
                    renderer: dropdownRenderer(dropdownOptions["RingSize"]),
                },
                "Ring size": {
                    data: "Ring size",
                    type: "dropdown",
                    source: ["A", "AB", "B", "C", "F", "AA", "BC", "G", "K", "L", "M", "YZ", "Z", "NA"],
                    renderer: dropdownRenderer(dropdownOptions["Ring size"]),
                },
                "Species Code": {
                    data: "Species Code",
                    type: "text",
                    renderer: (instance, td, row, col, prop, value, cellProperties) => {
                        SpeciesCodeRender(instance, td, row, col, prop, value, cellProperties, TableData.errorList['' + row + '_' + prop])
                    },
                },
                "Scientific Name": {
                    data: "Scientific Name",
                    type: "text",
                    renderer: (instance, td, row, col, prop, value, cellProperties) => {
                        SpeciesRender(instance, td, row, col, prop, value, cellProperties, TableData.errorList['' + row + '_' + prop])
                    },
                },
                "Sex": {
                    data: "Sex",
                    type: "dropdown",
                    source: ["", "Male", "Female", "Unknown"],
                    renderer: dropdownRenderer(dropdownOptions["Sex"]),
                },
                "Scx": {
                    data: "Scx",
                    type: "dropdown",
                    source: ["", "Male", "Female", "Unknown"],
                    renderer: dropdownRenderer(dropdownOptions["Scx"]),
                },
                "Reported By": {
                    data: "Reported By",
                    type: "text",
                },
                "Place of Recovery": {
                    data: "Place of Recovery",
                    type: "text",
                    renderer: (instance, td, row, col, prop, value, cellProperties) => {
                        LocalityRender(instance, td, row, col, prop, value, cellProperties, TableData.errorList['' + row + '_' + prop])
                    },
                },
                "Latitude": {
                    data: "Latitude",
                    type: "text",
                },
                "Longitude": {
                    data: "Longitude",
                    type: "text",
                },
                "Date of Recovery": {
                    data: "Date of Recovery",
                    type: "text",
                    renderer: (instance, td, row, col, prop, value, cellProperties) => {
                        DateRender(instance, td, row, col, prop, value, cellProperties, TableData.errorList['' + row + '_' + prop])
                    },
                },
                "Remarks": {
                    data: "Remarks",
                    type: "text",
                },
                "Date of Ringing": {
                    data: "Date of Ringing",
                    type: "text",
                    renderer: (instance, td, row, col, prop, value, cellProperties) => {
                        DateRender(instance, td, row, col, prop, value, cellProperties, TableData.errorList['' + row + '_' + prop])
                    },
                },
                "Place of Ringing": {
                    data: "Place of Ringing",
                    type: "text",
                    renderer: (instance, td, row, col, prop, value, cellProperties) => {
                        LocalityRender(instance, td, row, col, prop, value, cellProperties, TableData.errorList['' + row + '_' + prop])
                    },
                },
                "Ringer": {
                    data: "Ringer",
                    type: "text",
                },
                "QC Issues faced": {
                    data: "QC Issues faced",
                    type: "text",
                    // readOnly: true
                },
            };

            // Order columns: default headers first (in their original order), then dynamic columns in order, then other new columns
            list.sort((a, b) => {
                const indexA = defaultHeaders.indexOf(a);
                const indexB = defaultHeaders.indexOf(b);
                const dynamicIndexA = dynamicColumns.indexOf(a);
                const dynamicIndexB = dynamicColumns.indexOf(b);

                // Both are default headers - maintain their order
                if (indexA !== -1 && indexB !== -1) {
                    return indexA - indexB;
                }
                // Only A is a default header - A comes first
                else if (indexA !== -1) {
                    return -1;
                }
                // Only B is a default header - B comes first
                else if (indexB !== -1) {
                    return 1;
                }
                // Both are dynamic columns - maintain their specific order
                else if (dynamicIndexA !== -1 && dynamicIndexB !== -1) {
                    return dynamicIndexA - dynamicIndexB;
                }
                // Only A is a dynamic column - A comes before other new columns
                else if (dynamicIndexA !== -1) {
                    return -1;
                }
                // Only B is a dynamic column - B comes before other new columns
                else if (dynamicIndexB !== -1) {
                    return 1;
                }
                // Neither is a default or dynamic column - maintain their relative order (both go to end)
                else {
                    return 0;
                }
            });

            const t = await list.map(e => {
                if (typeof fixedList[e] !== 'undefined') {
                    return fixedList[e];
                }
                else if (e === "database_id") {
                    return {
                        data: e,
                        type: "text",
                        name: e,
                        readOnly: true,
                    };
                }
                else if (e === 'valid') {
                    return {
                        data: e,
                        type: "checkbox",
                        name: e,
                        readOnly: true
                    };
                }
                else if (e === "Ringing Record Found?") {
                    return {
                        data: e,
                        type: "checkbox",
                        name: e,
                        readOnly: true
                    };
                }
                else if(e === "Uploaded to Avifauna?")
                {
                    return {
                        data: e,
                        type: "checkbox",
                        name: e
                    };
                }
                else {
                    return {
                        data: e,
                        type: "text",
                        name: e
                    };
                }
            });

            // Add essential columns if not present
            const essentialColumns = ["Ring Size", "Reported By", "Place of Recovery",
                "Latitude", "Longitude", "Remarks", "Date of Ringing",
                "Place of Ringing", "QC Issues faced"];

            essentialColumns.forEach(col => {
                // Check against the actual list being used (not TableData.updated_headers which might be undefined)
                if (!list.includes(col)) {
                    if (fixedList[col]) {
                        t.push(fixedList[col]);
                        // Only push to updated_headers if it exists and is an array
                        if (TableData.updated_headers && Array.isArray(TableData.updated_headers)) {
                            TableData.updated_headers.push(col);
                        }
                    }
                }
            });

            setCols(t);
        }
    };

    const appStates = useContext(AppContext);

    // const updateErrorList = () => {
    //     let temp = [];
    //     for (const [key, value] of Object.entries(TableData.errorList)) {
    //         let message = value.obj.map(e => e.message + ", ").join();

    //         if (!(parseInt(value.row) < 0) && (typeof TableData.updated_headers.indexOf(value.col) !== 'undefined')) {
    //             temp.push({
    //                 col: TableData.updated_headers.indexOf(value.col),
    //                 row: value.row,
    //                 comment: { value: message }
    //             });
    //         }
    //     }
    //     setError(temp);
    // };

    const updateErrorList = () => {
        if (!TableData.errorList || !TableData.updated_headers) return;

        let temp = [];
        for (const [key, value] of Object.entries(TableData.errorList)) {
            // make sure value.obj is an array
            const messages = Array.isArray(value.obj)
                ? value.obj.map(e => e.message + ", ").join("")
                : "";
    
            // Only process valid row/col combinations
            if ( 
                !(parseInt(value.row) < 0) && 
                Array.isArray(TableData.updated_headers) && 
                TableData.updated_headers.indexOf(value.col) !== -1 
            ) { 
                temp.push({ 
                    col: TableData.updated_headers.indexOf(value.col), 
                    row: value.row, 
                    comment: { value: messages } 
                }); 
            } 
        } 
        setError(temp);
    };
    
    useEffect(() => {
        // Use updated_headers if available, otherwise use default headers
        // Sort headers to maintain default order + new columns at the end
        const rawList = (TableData?.updated_headers && TableData.updated_headers.length > 0) 
            ? TableData.updated_headers 
            : defaultHeaders;
        
        const list = sortHeaders(rawList);
        
        columnListBuilder(list);

        // Use updated_data if available, otherwise map from data or create empty rows
        if (typeof TableData.updated_data !== 'undefined' && TableData.updated_data.length > 0) {
            setRows(TableData.updated_data);
        } else if (typeof TableData.data !== 'undefined' && TableData.data.length > 0) {
            // Map data from expandable table to match the headers structure
            const mappedData = mapDataToExistingColumns(TableData.data, list);
            setRows(mappedData);
        } else {
            // Create 10 empty rows with all header keys
            const emptyRows = Array(10).fill(null).map(() => {
                const emptyRow = {};
                list.forEach(header => {
                    emptyRow[header] = "";
                });
                return emptyRow;
            });
            setRows(emptyRows);
        }

        if (typeof TableData.errorList !== 'undefined' && typeof TableData.updated_headers !== 'undefined') {
            updateErrorList();
        }
    }, [TableData]);

    // const cellEditedCallback = (param) => {
    //     let arr = param?.pop();
    //     if (typeof arr !== 'undefined') {
    //         delete (TableData.errorList["" + arr[0] + "_" + arr[1]]);
    //         updatedRowsCallBack(arr[0]);
    //         updateErrorList();
    //     }
    // };
    const cellEditedCallback = (param) => {
        if (!param) return;

        let arr = param?.pop();
        if (typeof arr !== 'undefined') {
            if (TableData.errorList) {
                delete (TableData.errorList["" + arr[0] + "_" + arr[1]]);
            }
            // delete (TableData.errorList["" + arr[0] + "_" + arr[1]]);
            updatedRowsCallBack(arr[0]);
            if(TableData.errorList && TableData.updated_headers) {
                updateErrorList();
            }
        }
    };

    const afterAutoFill = (param) => {
        console.log(param);
    };

    // const handleColAdd = (key, selection, clickEvent) => {
    //     const colName = prompt("Enter Column Name");
    //     if(!colName) return;

    //     const exists = cols.map(e => e.data).find(e => e.toLowerCase() === colName.toLowerCase());

    //     if (typeof exists !== 'undefined') {
    //         window.alert("Column Name Already Exists");
    //     } else {
    //         const tableHeaders = passedRef.current.hotInstance.getColHeader();
    //         const arrayOfarray = passedRef.current.hotInstance.getData();
    //         let data = arrayOfarray.map(e => {
    //             let obj = {};
    //             tableHeaders.forEach((key, index) => {
    //                 obj[key] = e[index];
    //             });
    //             if (obj["Ring No"] !== null) {
    //                 return obj;
    //             }
    //         });
    //         data = data.filter(e => typeof e !== 'undefined');

    //         const alreadyThere = TableData.updated_headers.find(e => e.toLowerCase() === colName.toLowerCase());
    //         if (typeof alreadyThere !== 'undefined') {
    //             data = data.map((e, index) => {
    //                 return { ...e, [colName]: TableData.updated_data[index][colName] };
    //             });
    //             columnListBuilder([...cols.map(e => e.data), colName]);
    //             setRows(data);
    //         } else {
    //             data = data.map(e => { return { ...e, [colName]: "" }; });
    //             columnListBuilder([...cols.map(e => e.data), colName]);
    //             setRows(data);
    //         }
    //     }
    // };
    const handleColAdd = (key, selection, clickEvent) => {
        const colName = prompt("Enter Column Name");
    
        if (!colName) return; // Guard against null/empty prompt
    
        const exists = cols.map(e => e.data).find(e => e.toLowerCase() === colName.toLowerCase());
    
        if (typeof exists !== 'undefined') {
            window.alert("Column Name Already Exists");
        } else {
            const tableHeaders = passedRef.current.hotInstance.getColHeader();
            const arrayOfarray = passedRef.current.hotInstance.getData();
            let data = arrayOfarray.map(e => {
                let obj = {};
                tableHeaders.forEach((key, index) => {
                    obj[key] = e[index];
                });
                if (obj["Ring No"] !== null) {
                    return obj;
                }
            });
            data = data.filter(e => typeof e !== 'undefined');
    
            // Guard against undefined updated_headers
            const alreadyThere = TableData.updated_headers 
                ? TableData.updated_headers.find(e => e.toLowerCase() === colName.toLowerCase())
                : undefined;
    
            if (typeof alreadyThere !== 'undefined') {
                data = data.map((e, index) => {
                    return { ...e, [colName]: TableData.updated_data?.[index]?.[colName] ?? "" };
                });
                columnListBuilder([...cols.map(e => e.data), colName]);
                setRows(data);
            } else {
                data = data.map(e => { return { ...e, [colName]: "" }; });
                columnListBuilder([...cols.map(e => e.data), colName]);
                setRows(data);
            }
        }
    };

    const handleColDelete = (key, selection, clickEvent) => {
        clickEvent.preventDefault();

        const colName = prompt("Alert this operation removes data from orginal table and cannot be changed, Enter Column Name:");
        if(!colName) return;
        const exists = cols.map(e => e.data).find(e => e.toLowerCase() === colName.toLowerCase());

        if (typeof exists === 'undefined') {
            window.alert("Column Name Does Not Exists");
        } else {
            const tableHeaders = passedRef.current.hotInstance.getColHeader();
            const arrayOfarray = passedRef.current.hotInstance.getData();
            let data = arrayOfarray.map(e => {
                let obj = {};
                tableHeaders.forEach((key, index) => {
                    obj[key] = e[index];
                });
                if (obj["Ring No"] !== null) {
                    return obj;
                }
            });
            data = data.filter(e => typeof e !== 'undefined');

            data = data.map(e => { 
                const {[colName]: _, ...rest} = e;
                return rest; });
            columnListBuilder([...cols.map(e => e.data).filter(e => e !== colName)]);
            setRows(data);
        }
    };

    // const handleCreateNewRow = (index, number) => {
    //     passedRef.current.hotInstance.setDataAtRowProp(index, 'id', -1);
    //     if (TableData.updated_headers.includes("database_id")) {
    //         passedRef.current.hotInstance.setDataAtRowProp(index, 'database_id', -1);
    //     }
    // };
    const handleCreateNewRow = (index, number) => {
        passedRef.current.hotInstance.setDataAtRowProp(index, 'id', -1);
        if (TableData.updated_headers && TableData.updated_headers.includes("database_id")) {
            passedRef.current.hotInstance.setDataAtRowProp(index, 'database_id', -1);
        }
    };

    // const handleDelRows = (index, amount, array) => {
    //     if (TableData.updated_headers.includes("database_id")) {
    //         let id = passedRef.current.hotInstance.getDataAtRowProp(index, "database_id");
    //         if (id !== -1) delRowsCallback(id);
    //     }
    // };
    const handleDelRows = (index, amount, array) => {
        if (TableData.updated_headers && TableData.updated_headers.includes("database_id")) {
            let id = passedRef.current.hotInstance.getDataAtRowProp(index, "database_id");
            if (id !== -1) delRowsCallback(id);
        }
    };

    const handleScientificNameAutoFill = async (key, selection, clickEvent) => {
        let performAction = window.confirm("This action will overwrite the Scientific Name column? Do you wish to perform the action?");

        if (performAction) {
            let mappingData = {};
            if (cacheManager.doesExists("speciesMap")) {
                mappingData = cacheManager.getEntry("speciesMap");
            } else {
                let res = await getObject.caller("/getSpeciesTable");
                let _data = await res.json();
                mappingData = _data;
                cacheManager.setEntry("speciesMap", _data);
            }
            mappingData = mappingData.data;
            let map = {};
            mappingData.forEach(e => {
                const scientificName = e["Scientific Name"] || e["Species Name"];
                map[e["Species Code"] + "-" + e["Race Code"]] =
                    scientificName + '' + (e["Race Name"] === "Nil (no race)" ? "" : (" " + e["Race Name"]));
                map[e["Species Code"]] = scientificName;
                map[e["Species Code"] + "-0"] = scientificName;
            });

            const hotInstance = passedRef.current.hotInstance;
            const arr = hotInstance.getDataAtProp("Species Code");
            arr.forEach((e, indx) => {
                if (e.trim().length !== 0) {
                    let parame = e.replace(/^0+(?=[1-9])/, '').trim();
                    if (/^[0-9]{1,4}-[0-9]{1}$/.test(parame) || /^[0-9]{1,4}$/.test(parame)) {
                        hotInstance.setDataAtRowProp(indx, "Scientific Name", map[parame] || "");
                    }
                }
            });
        }
    };

    const autoFillRecoveryLocation = (key, selection, clickEvent) => {
        const hot = passedRef.current.hotInstance;
        const selected = hot.getSelected();

        if (selected && selected.length > 0) {
            const [startRow, startCol, endRow, endCol] = selected[0];

            // Check if selection includes "Place of Recovery" column
            const recoveryColIndex = cols.map(e => e.data).indexOf("Place of Recovery");

            if (startCol <= recoveryColIndex && endCol >= recoveryColIndex) {
                const location = prompt("Enter recovery location to auto-fill:");
                if (location) {
                    for (let row = startRow; row <= endRow; row++) {
                        hot.setDataAtCell(row, recoveryColIndex, location);
                    }
                }
            }
        }
    };

    const handleUndo = () => {
        const hot = passedRef.current.hotInstance;
        const undoRedo = hot.getPlugin('undoRedo');
        if (undoRedo && undoRedo.isEnabled()) {
            undoRedo.undo();
        }
    };
    
    const handleRedo = () => {
        const hot = passedRef.current.hotInstance;
        const undoRedo = hot.getPlugin('undoRedo');
        if (undoRedo && undoRedo.isEnabled()) {
            undoRedo.redo();
        }
    };

    return (
        <div
            style={{
                height: "100%",
                maxHeight: "100%",
                overflow: "auto",
            }}
        >
            <HotTable
                ref={passedRef}
                data={rows}
                colHeaders={cols.map((e) => e.data)}
                columns={cols}
                width={"100%"}
                height={"auto"}
                autoWrapRow={true}
                autoWrapCol={true}
                autoColumnSize={true}
                dropdownMenu={true}
                multiColumnSorting={true}
                manualColumnFreeze={true}
                filters={true}
                manualColumnResize={true}
                comments={true}
                cell={error}
                afterChange={cellEditedCallback}
                afterAutofill={afterAutoFill}
                fixedColumnsStart={1}
                allowInsertColumn={appStates.getAuthStat() === "Admin"}
                afterCreateRow={handleCreateNewRow}
                allowInsertRow={
                    appStates.getAuthStat() === "Admin" ||
                    appStates.getAuthStat() === "Researcher"
                }
                allowRemoveColumn={appStates.getAuthStat() === "Admin"}
                allowRemoveRow={appStates.getAuthStat() === "Admin"}
                beforeRemoveRow={handleDelRows}
                contextMenu={{
                    items: {
                        row_above: {
                            disabled() {
                                return !(
                                    appStates.getAuthStat() === "Admin" ||
                                    appStates.getAuthStat() === "Researcher"
                                );
                            },
                        },
                        row_below: {
                            disabled() {
                                return !(
                                    appStates.getAuthStat() === "Admin" ||
                                    appStates.getAuthStat() === "Researcher"
                                );
                            },
                        },
                        col_addition: {
                            name: "Add Column",
                            disabled() {
                                return !(
                                    appStates.getAuthStat() === "Admin" ||
                                    appStates.getAuthStat() === "Researcher"
                                );
                            },
                            callback(key, selection, clickEvent) {
                                clickEvent.preventDefault();
                                handleColAdd(key, selection, clickEvent);
                            },
                        },
                        remove_row: {
                            disabled() {
                                return !(
                                    appStates.getAuthStat() === "Admin" ||
                                    appStates.getAuthStat() === "Researcher"
                                );
                            },
                        },
                        col_removal: {
                            name: "Remove Column",
                            disabled() {
                                return !(
                                    appStates.getAuthStat() === "Admin" ||
                                    appStates.getAuthStat() === "Researcher"
                                );
                            },
                            callback(key, selection, clickEvent) {
                                handleColDelete(key, selection, clickEvent);
                            },
                        },
                        sp1: "---------",
                        undo: {},
                        redo: {},
                        sp2: "---------",
                        freeze_column: {},
                        unfreeze_column: {},
                        sp3: "---------",
                        autoScientificName: {
                            name() {
                                return "<b>Auto Fill Scientific Name</b>";
                            },
                            callback(key, selection, clickEvent) {
                                clickEvent.preventDefault();
                                handleScientificNameAutoFill(key, selection, clickEvent);
                            },
                            hidden() {
                                let hot = passedRef.current.hotInstance;
                                let spIndex = cols.map((e) => e.data).indexOf("Scientific Name");
                                let lastCell = hot.getSelectedLast();
                                return lastCell[1] !== spIndex;
                            },
                        },
                        autoRecoveryLocation: {
                            name() {
                                return "<b>Auto Fill Recovery Location</b>";
                            },
                            callback(key, selection, clickEvent) {
                                clickEvent.preventDefault();
                                autoFillRecoveryLocation(key, selection, clickEvent);
                            },
                            hidden() {
                                let hot = passedRef.current.hotInstance;
                                let recoveryIndex = cols.map((e) => e.data).indexOf("Place of Recovery");
                                let lastCell = hot.getSelectedLast();
                                return lastCell[1] !== recoveryIndex;
                            },
                        },
                    },
                }}
                rowHeights={30}
                licenseKey="non-commercial-and-evaluation"
            ></HotTable>
        </div>
    );
});

export default HandsOnTableResighting;